import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ImageGalleryContainer from 'containers/ImageGalleryContainer'
import { Button } from 'components/ui/button'
import { Upload } from 'lucide-react'
import ImageUploadWizard, {
	commitImageUpload,
	locationImageAdapter,
} from 'components/ImageUploadWizard'
import { getImages } from 'slices/locationImageSlice'
import LocationImagesEmptyState from 'components/LocationImagesEmptyState'
import Spinner from 'components/Spinner'
import Message from 'components/Message'

const LocationImageGallery = ({ user, location, project }) => {
	const locationId = location._id
	// May legitimately be undefined on the Location Images route. Left as-is:
	// getMyLocationImages returns every image for the location when projectid
	// is absent, which the portfolio rollup depends on.
	const projectId = project._id
	const userId = user._id

	const dispatch = useDispatch()
	const [wizardOpen, setWizardOpen] = useState(false)

	const { entities: imageEntities, ids: imageIds, isLoading, error } = useSelector(
		state => state.locationImages,
	)

	useEffect(() => {
		dispatch(getImages({ locationid: locationId, projectid: projectId, userid: userId }))
	}, [dispatch, locationId, projectId, userId])

	const locationImages = imageIds.map(id => imageEntities[id])
	const hasImages = locationImages && locationImages.length > 0

	const uploadTarget = useMemo(
		() => ({
			entityLabel: location.assetName || location.address || 'this location',
			folder: `mdt/locations/${locationId}`,
			onCommit: (commit, state) =>
				commitImageUpload(
					commit,
					locationImageAdapter({ dispatch, userId, locationId, projectId }),
					{},
					state,
				),
		}),
		[dispatch, userId, locationId, projectId, location.assetName, location.address],
	)

	if (isLoading) return <Spinner containerHeight="50vh" />
	if (error) return <Message variant="danger">{error}</Message>

	return (
		<div>
			{hasImages && (
				<div className="mb-6 flex flex-wrap items-center justify-between gap-4">
					<div className="flex flex-col gap-1">
						<h2 className="m-0 text-2xl font-bold">Images</h2>
						<span className="text-[0.95rem] text-muted-foreground">
							Maintain a visual record of this location with organized photo uploads.
						</span>
					</div>
					<Button type="button" variant="primary" onClick={() => setWizardOpen(true)}>
						<span className="flex items-center gap-2">
							<Upload className="h-4 w-4" />
							Upload Images
						</span>
					</Button>
				</div>
			)}

			{hasImages ? (
				<ImageGalleryContainer
					locationid={locationId}
					projectid={projectId}
					showPlaceholder={false}
				/>
			) : (
				<LocationImagesEmptyState onUploadClick={() => setWizardOpen(true)} />
			)}

			<ImageUploadWizard
				open={wizardOpen}
				onClose={() => setWizardOpen(false)}
				target={uploadTarget}
			/>
		</div>
	)
}

export default LocationImageGallery
